// What the billing screen shows, separate from how, so the rules are tested without a browser.
import type { BillingDto, BillingStatusDto, FeatureCode, PlanCode } from "@imagora/contracts";
import { DEFAULT_PLAN, FEATURE_CODES, PLAN_CODES, PLAN_FEATURES, PLANS, planMonthlyTotalMinor } from "@imagora/contracts/pricing";

export function formatMoney(minor: number, currency: string, lang: string): string {
  return new Intl.NumberFormat(lang, { style: "currency", currency, minimumFractionDigits: minor % 100 === 0 ? 0 : 2 }).format(minor / 100);
}

export type BillingTone = "success" | "warning" | "danger" | "neutral";

/** The word and colour beside the plan. Past due within the grace is a warning: nothing has changed yet. */
export function statusTone(billing: Pick<BillingDto, "status" | "newWorkAllowed" | "graceUntil">): BillingTone {
  if (!billing.newWorkAllowed) return "danger";
  const status = statusWord(billing);
  if (status === "active" || status === "trialing") return "success";
  if (status === "past_due" || status === "unpaid" || status === "incomplete") return "warning";
  return "neutral";
}

/**
 * The word beside the plan. Stripe's events can arrive in any order, so a failed payment may be known before the subscription says past_due:
 * the screen must not say "active" above a line about a failed payment.
 */
export function statusWord(billing: Pick<BillingDto, "status" | "newWorkAllowed" | "graceUntil">): BillingDto["status"] {
  const late = Boolean(billing.graceUntil) || !billing.newWorkAllowed;
  return late && (billing.status === "active" || billing.status === "trialing") ? "past_due" : billing.status;
}

/** A subscription already exists: the provider's portal changes it, a second checkout would charge twice. */
export const hasSubscription = (status: BillingDto["status"]): boolean => status !== "none" && status !== "canceled" && status !== "incomplete";

/** The total as the person types the number of locations: the plan's price, with the volume discount the count reaches. */
export function estimate(locations: number, plan: PlanCode = DEFAULT_PLAN): number | null {
  return Number.isInteger(locations) && locations >= 1 && locations <= 500 ? planMonthlyTotalMinor(plan, locations) : null;
}

export interface PlanCard {
  code: PlanCode;
  monthlyMinor: number;
  aiIncludedUsd: number;
  emailsIncludedPerLocation: number;
  /** The plan before it, whose features this one has too; null for the first. */
  builtOn: PlanCode | null;
  /** Only what this plan adds, so each card stays short. */
  adds: FeatureCode[];
  current: boolean;
}

/** The three plans, from the same catalog the API gates with. */
export function planCards(current: PlanCode): PlanCard[] {
  return PLAN_CODES.map((code, index) => ({
    code,
    monthlyMinor: PLANS[code].monthlyMinor,
    aiIncludedUsd: PLANS[code].aiIncludedUsd,
    emailsIncludedPerLocation: PLANS[code].emailsIncludedPerLocation,
    builtOn: PLAN_CODES[index - 1] ?? null,
    adds: FEATURE_CODES.filter((feature) => PLAN_FEATURES[feature].minPlan === code),
    current: code === current,
  }));
}

/** How full an included amount is, 0 to 1, for the bar beside it. Nothing included and nothing used is empty, not full. */
export function share(used: number, included: number): number {
  if (included <= 0) return used > 0 ? 1 : 0;
  return Math.min(1, Math.max(0, used / included));
}

export interface Banner {
  /** The message is `tCode(prefix, code)`. */
  prefix: "billing.banner" | "trial";
  code: string;
  tone: "warning" | "danger";
}

/** What the banner over every page has to say, or null when there is nothing. A reason, never a threat. */
export function bannerFor(status: { mode: string; newWorkBlockedBy: string | null; graceUntil: string | null }): Banner | null {
  if (status.mode !== "stripe") return null;
  if (status.newWorkBlockedBy === "subscription_required") return { prefix: "billing.banner", code: "subscriptionRequired", tone: "warning" };
  // The trial's first payment failed: no grace, so none of the renewal's 14 days; the leads stay and a payment reopens everything.
  if (status.newWorkBlockedBy === "trial_unpaid") return { prefix: "trial", code: "banner", tone: "danger" };
  if (status.newWorkBlockedBy) return { prefix: "billing.banner", code: `blocked.${status.newWorkBlockedBy}`, tone: "danger" };
  if (status.graceUntil) return { prefix: "billing.banner", code: "grace", tone: "warning" };
  return null;
}

/** The trial's first payment failed: the workspace opens on the payment screen and nothing else (decision 8 of s10-signup). */
export function isStopped(status: { stoppedAt?: string | null; newWorkBlockedBy?: string | null } | null | undefined): boolean {
  return Boolean(status && (status.stoppedAt || status.newWorkBlockedBy === "trial_unpaid"));
}

/**
 * The stopped screen's way to pay. The portal pays the failed invoice of a live subscription. Once the subscription is gone (the cancel on
 * day 14 after the stop, `cancelLongStopped`), the portal can't start a new one: the Owner gets „Abonează-te”, the payment step's Checkout
 * without the trial. Anyone else is told to ask the Owner.
 */
export function stoppedPayment(status: Pick<BillingStatusDto, "status">, owner: boolean): "portal" | "subscribe" | "owner_only" {
  if (!owner) return "owner_only";
  return status.status === "canceled" || status.status === "none" ? "subscribe" : "portal";
}

/** A 402 of the stop: the API's `billing_required` with the reason `trial_unpaid`. The shell then draws the payment screen, not an error. */
export function isStopRefusal(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { code, details } = error as { code?: unknown; details?: unknown };
  return code === "billing_required" && typeof details === "object" && details !== null && (details as { reason?: unknown }).reason === "trial_unpaid";
}

/**
 * The date on the subscription card and what it is: the end for one that is leaving, the first payment in the trial, the renewal otherwise.
 * In the trial both are the trial's end (s10-trial-b): a workspace canceled in it ends then, with nothing charged.
 */
export function subscriptionEnd(billing: Pick<BillingDto, "status" | "trial" | "currentPeriodEnd" | "cancelAtPeriodEnd">): { kind: "endsOn" | "firstPayment" | "renewsOn"; date: string } | null {
  const trialEnd = billing.status === "trialing" ? (billing.trial?.endsAt ?? null) : null;
  const date = trialEnd ?? billing.currentPeriodEnd;
  if (!date) return null;
  return { kind: billing.cancelAtPeriodEnd ? "endsOn" : trialEnd ? "firstPayment" : "renewsOn", date };
}

/** The trial's days and e-mails on the billing screen, while the subscription is in it. */
export function runningTrial(billing: Pick<BillingDto, "status" | "trial">): (NonNullable<BillingDto["trial"]> & { endsAt: string; daysLeft: number }) | null {
  const trial = billing.trial;
  if (billing.status !== "trialing" || !trial?.endsAt) return null;
  return { ...trial, endsAt: trial.endsAt, daysLeft: Math.max(0, trial.daysLeft ?? 0) };
}

/**
 * Back from Stripe's page, before its event: the status comes only from Stripe, so the screen waits and says so rather than offering a
 * second Checkout. A trial refused for its card is no wait: the screen offers to subscribe without one.
 */
export function activating(returned: string | null, billing: Pick<BillingDto, "status" | "trial">): boolean {
  return returned === "done" && !hasSubscription(billing.status) && billing.trial?.refusedBy !== "card";
}

/** How long the screen waits for Stripe's event before it offers the payment step again. */
export const ACTIVATING_FOR_MS = 120_000;
